
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
            <p className="text-lg text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We collect information you provide directly to us, such as when you create an account, 
                fill out a form, or contact us. This may include your name, email address, phone number, 
                practice information, and other details you choose to provide.
              </p>
              <p className="text-gray-700 mb-4">
                We also automatically collect certain information about your device and how you interact 
                with our services, including your IP address, browser type, operating system, and usage patterns.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, security alerts, and support messages</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Communicate with you about products, services, and events</li>
                <li>Monitor and analyze trends, usage, and activities</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">SMS Messaging</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Overview of SMS Services</h3>
              <p className="text-gray-700 mb-4">
                Our SMS messaging services, delivered through a 10-Digit Long Code (10DLC), enable us to send 
                text messages directly to users who have opted in to receive such communications. This section 
                of the Privacy Policy outlines how we handle personal data in relation to our SMS services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Consent and Data Collection</h3>
              <p className="text-gray-700 mb-4">
                <strong>User Consent:</strong> We collect phone numbers and related personal data for SMS messaging 
                only from users who have given their explicit consent. Consent is typically obtained through 
                sign-up forms, online agreements, or other opt-in methods.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Data Collection:</strong> The personal data collected for SMS services may include your 
                mobile phone number, name, and other relevant information required to deliver the service effectively.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Use of Personal Data</h3>
              <p className="text-gray-700 mb-4">
                <strong>Purpose of Data Use:</strong> The personal data collected is used exclusively for the 
                purpose of sending SMS messages as per the user's consent. This may include promotional messages, 
                alerts, updates, and other communications.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Data Processing:</strong> We take all necessary steps to ensure that personal data is 
                processed in accordance with applicable laws and regulations, and only for the duration necessary 
                to fulfill the intended messaging services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Sharing and Disclosure</h3>
              <p className="text-gray-700 mb-4">
                <strong>Third-Party Access:</strong> We do not sell or rent personal data to third parties. 
                Access to personal data is strictly limited to what is necessary to provide our SMS services.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Compliance with Law:</strong> We may disclose personal data if required by law or if 
                necessary to protect our rights or comply with judicial proceedings, court orders, or legal 
                processes related to our services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">User Rights</h3>
              <p className="text-gray-700 mb-4">
                <strong>Opt-Out:</strong> Users can opt out of our SMS messaging service at any time by replying 
                with a standard keyword like "STOP".
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Access and Correction:</strong> Users have the right to access their personal data and 
                request corrections or deletions as applicable.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Information Sharing</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties without 
                your consent, except as described in this policy. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>With service providers who assist us in operating our website and conducting our business</li>
                <li>When we believe disclosure is necessary to comply with the law or protect our rights</li>
                <li>In connection with a merger, acquisition, or sale of assets</li>
                <li>With your explicit consent</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
                over the internet or electronic storage is completely secure.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Rights</h2>
              <p className="text-gray-700 mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>The right to access your personal information</li>
                <li>The right to correct inaccurate information</li>
                <li>The right to delete your personal information</li>
                <li>The right to restrict processing of your information</li>
                <li>The right to data portability</li>
                <li>The right to object to processing</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to collect information about your browsing 
                activities and to provide you with a more personalized experience. You can control cookies 
                through your browser settings.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Concierge Medical Marketing LLC</strong>
                </p>
                <p className="text-gray-700 mb-2">
                  Email: <a href="mailto:info@conciergemd.marketing" className="text-blue-600 hover:text-blue-800">info@conciergemd.marketing</a>
                </p>
                <p className="text-gray-700">
                  Phone: <a href="tel:7723042420" className="text-blue-600 hover:text-blue-800">(772) 304-2420</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
